import { cn } from './cn';

/**
 * The category kicker (`.cat`), the BREAKING tag, and the LIVE pill — three things the
 * design draws the same way at different colours.
 *
 * `tone="category"` takes the colour an editor set on the category (PRD §8) through an
 * inline custom property rather than a Tailwind class, because the value comes from the
 * database and cannot be in the compiled stylesheet.
 */
export type BadgeTone = 'red' | 'solid' | 'live' | 'category' | 'muted';

export function Badge({
  tone = 'red',
  color,
  children,
  className,
}: {
  tone?: BadgeTone;
  /** Hex from `categories.color`; only read when `tone="category"`. */
  color?: string | null;
  children: React.ReactNode;
  className?: string;
}) {
  const tones: Record<BadgeTone, string> = {
    red: 'text-red',
    solid: 'bg-red text-white px-2.5 py-1 rounded-xs',
    live: 'bg-red text-white px-2.5 py-1 rounded-xs inline-flex items-center gap-1.5',
    category: '',
    muted: 'text-muted',
  };

  return (
    <span
      className={cn(
        'text-2xs font-extrabold uppercase tracking-wider',
        tones[tone],
        tone === 'category' && 'text-[color:var(--badge-color,var(--red))]',
        className,
      )}
      style={
        tone === 'category' && color
          ? ({ '--badge-color': color } as React.CSSProperties)
          : undefined
      }
    >
      {tone === 'live' && <span className="h-1.5 w-1.5 rounded-full bg-white animate-blink" />}
      {children}
    </span>
  );
}
